import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import {
  applyEventSnapshotRestore,
  deleteEventSnapshot,
  getEventSnapshots,
  previewEventSnapshotRestore,
  saveEventSnapshot
} from '../../features/event/eventSlice';
import PrimaryButton from '../ui/buttons/primaryButton';
import EventSnapshotPreview from './eventSnapshotPreview';

const number = (value) => Number(value) || 0;

const snapshotTypeLabel = (snapshot) => {
  if (snapshot?.source === 'restore') {
    return 'Before restore';
  }
  return snapshot?.type === 'manual' ? 'Manual' : 'Automatic';
};

const snapshotChipColor = (snapshot) => {
  if (snapshot?.source === 'restore') {
    return 'warning';
  }
  return snapshot?.type === 'manual' ? 'primary' : 'default';
};

const actorLabel = (snapshot) => snapshot?.actor?.name || snapshot?.actor?.email || 'Unknown';

const countsSummary = (snapshot) => {
  const counts = snapshot?.counts || {};
  const parts = [];
  if (number(counts.sections)) {
    parts.push(`${counts.sections} section${number(counts.sections) === 1 ? '' : 's'}`);
  }
  if (number(counts.teams)) {
    parts.push(`${counts.teams} team${number(counts.teams) === 1 ? '' : 's'}`);
  }
  if (number(counts.players)) {
    parts.push(`${counts.players} player${number(counts.players) === 1 ? '' : 's'}`);
  }
  const games = number(counts.games) + number(counts.deletedGames);
  if (games) {
    parts.push(`${games} game${games === 1 ? '' : 's'}`);
  }
  if (number(counts.brackets)) {
    parts.push(`${counts.brackets} bracket${number(counts.brackets) === 1 ? '' : 's'}`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'Empty event';
};

const errorMessage = (error) =>
  error?.message || error?.error || 'The request could not be completed.';

const errorAlert = (error) => {
  if (!error) {
    return null;
  }
  const written = error.details?.written;
  const partialText =
    error.details?.partialFailure && written
      ? `Some changes were written: ${number(written.games)} games, ${number(
          written.bracketDocuments
        )} bracket documents${written.eventDocument ? ', and the event' : ''}.`
      : '';
  const before = error.details?.beforeSnapshot;

  return (
    <Alert severity={error.severity || 'error'}>
      <Typography variant="body2">{errorMessage(error)}</Typography>
      {partialText ? (
        <Typography variant="caption" display="block">
          {partialText}
        </Typography>
      ) : null}
      {before ? (
        <Typography variant="caption" display="block">
          A safety restore point was saved first: &quot;{before.name}&quot;.
        </Typography>
      ) : null}
    </Alert>
  );
};

const EventHistoryDialog = ({ open, onClose, eventId, eventName, onRestored }) => {
  const dispatch = useDispatch();

  const [step, setStep] = useState('list');
  const [snapshots, setSnapshots] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const busy = saving || previewLoading || applying || deleting;

  const loadSnapshots = useCallback(
    async ({ reset = false, offset = 0 } = {}) => {
      if (!eventId) {
        return;
      }
      setListLoading(true);
      setListError(null);
      try {
        const data = await dispatch(getEventSnapshots({ eventId, limit: 50, offset })).unwrap();
        setSnapshots((previous) => {
          if (reset) {
            return data.snapshots || [];
          }
          const seen = new Set(previous.map((snapshot) => snapshot.id));
          return [
            ...previous,
            ...(data.snapshots || []).filter((snapshot) => !seen.has(snapshot.id))
          ];
        });
        setTotal(number(data.total));
        setHasMore(Boolean(data.hasMore));
      } catch (error) {
        setListError(error);
      } finally {
        setListLoading(false);
      }
    },
    [dispatch, eventId]
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    setStep('list');
    setSnapshots([]);
    setTotal(0);
    setHasMore(false);
    setListError(null);
    setName('');
    setSelected(null);
    setPreview(null);
    setResult(null);
    setActionError(null);
    setDeleteTarget(null);
    setDeleteError(null);
  }, [open]);

  useEffect(() => {
    if (open && eventId) {
      loadSnapshots({ reset: true, offset: 0 });
    }
  }, [open, eventId, loadSnapshots]);

  const handleClose = () => {
    if (!applying) {
      onClose();
    }
  };

  const handleSave = async () => {
    if (!eventId || saving) {
      return;
    }
    setSaving(true);
    setActionError(null);
    try {
      await dispatch(saveEventSnapshot({ eventId, name: name.trim() })).unwrap();
      setName('');
      await loadSnapshots({ reset: true, offset: 0 });
    } catch (error) {
      setActionError(error);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async (snapshot) => {
    if (!snapshot?.id || previewLoading) {
      return;
    }
    setSelected(snapshot);
    setPreview(null);
    setResult(null);
    setActionError(null);
    setStep('review');
    setPreviewLoading(true);
    try {
      const data = await dispatch(
        previewEventSnapshotRestore({ eventId, snapshotId: snapshot.id })
      ).unwrap();
      setPreview(data);
    } catch (error) {
      setActionError(error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApply = async () => {
    if (!selected?.id || !preview?.previewHash || applying) {
      return;
    }
    setApplying(true);
    setActionError(null);
    try {
      const data = await dispatch(
        applyEventSnapshotRestore({
          eventId,
          snapshotId: selected.id,
          previewHash: preview.previewHash
        })
      ).unwrap();

      if (data.alreadyApplied) {
        setStep('list');
        setActionError({
          severity: 'info',
          message: 'This restore point already matches the event. Nothing was written.'
        });
        await loadSnapshots({ reset: true, offset: 0 });
        return;
      }

      setResult(data);
      setStep('done');
      if (onRestored) {
        onRestored();
      }
      await loadSnapshots({ reset: true, offset: 0 });
    } catch (error) {
      if (error?.code === 'EVENT_CHANGED') {
        try {
          const fresh = await dispatch(
            previewEventSnapshotRestore({ eventId, snapshotId: selected.id })
          ).unwrap();
          setPreview(fresh);
          setActionError({
            severity: 'info',
            message:
              'The event changed since the preview. The preview has been refreshed; review it before restoring.'
          });
        } catch (refreshError) {
          setActionError(refreshError);
        }
      } else {
        setActionError(error);
        if (error?.details?.partialFailure || error?.details?.beforeSnapshot) {
          await loadSnapshots({ reset: true, offset: 0 });
        }
      }
    } finally {
      setApplying(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id || deleting) {
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await dispatch(deleteEventSnapshot({ eventId, snapshotId: deleteTarget.id })).unwrap();
      setDeleteTarget(null);
      await loadSnapshots({ reset: true, offset: 0 });
    } catch (error) {
      setDeleteError(error);
    } finally {
      setDeleting(false);
    }
  };

  const renderList = () => (
    <Stack gap={2}>
      <Typography variant="body2" color="text.secondary">
        Restore points are saved automatically before risky changes. You can also save one now, then
        restore the whole event later if a change spoils the draw, setup or results.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          fullWidth
          label="Restore point name (optional)"
          value={name}
          onChange={(event) => setName(event.target.value)}
          inputProps={{ maxLength: 120 }}
          disabled={saving}
        />
        <PrimaryButton
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ flexShrink: 0 }}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Save restore point'}
        </PrimaryButton>
      </Stack>

      {actionError ? errorAlert(actionError) : null}
      {listError ? errorAlert(listError) : null}

      <Divider />

      <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1}>
        <Typography variant="subtitle2">Restore points</Typography>
        <Typography variant="caption" color="text.secondary">
          {total} total
        </Typography>
      </Stack>

      {listLoading && snapshots.length === 0 ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : null}

      {!listLoading && snapshots.length === 0 ? (
        <Box py={4} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            No restore points yet. Save one above, or make a change and one will be saved first.
          </Typography>
        </Box>
      ) : null}

      <Stack gap={1.5}>
        {snapshots.map((snapshot) => (
          <Box
            key={snapshot.id}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1.5}>
              <Box minWidth={0}>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                  <Chip
                    size="small"
                    label={snapshotTypeLabel(snapshot)}
                    color={snapshotChipColor(snapshot)}
                  />
                  <Typography variant="subtitle2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                    {snapshot.name}
                  </Typography>
                </Stack>
                {snapshot.label && snapshot.label !== snapshot.name ? (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {snapshot.label}
                  </Typography>
                ) : null}
                <Typography variant="caption" color="text.secondary" display="block">
                  {dayjs(snapshot.createdAtMs).format('DD MMM YYYY HH:mm')} · {actorLabel(snapshot)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {countsSummary(snapshot)}
                </Typography>
                {number(snapshot.restoreCount) > 0 ? (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Restored {snapshot.restoreCount} time
                    {number(snapshot.restoreCount) === 1 ? '' : 's'}
                  </Typography>
                ) : null}
              </Box>
              <Stack direction="row" gap={1} flexWrap="wrap" alignItems="flex-start" flexShrink={0}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RestoreIcon />}
                  onClick={() => handlePreview(snapshot)}
                  disabled={busy}
                >
                  Restore
                </Button>
                {snapshot.type === 'manual' ? (
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    disabled={busy}
                    onClick={() => {
                      setDeleteError(null);
                      setDeleteTarget(snapshot);
                    }}
                  >
                    Delete
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          </Box>
        ))}
      </Stack>

      {hasMore ? (
        <Box display="flex" justifyContent="center">
          <Button
            onClick={() => loadSnapshots({ offset: snapshots.length })}
            disabled={listLoading}
          >
            {listLoading ? <CircularProgress size={20} /> : 'Load older restore points'}
          </Button>
        </Box>
      ) : null}
    </Stack>
  );

  const renderReview = () => (
    <Stack gap={2}>
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>
          {selected?.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {selected ? dayjs(selected.createdAtMs).format('DD MMM YYYY HH:mm') : ''} ·{' '}
          {selected ? snapshotTypeLabel(selected) : ''} · {selected ? actorLabel(selected) : ''}
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary">
        A safety restore point of the current event will be saved first, so this restore can be
        undone.
      </Typography>

      {actionError ? errorAlert(actionError) : null}

      {previewLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <EventSnapshotPreview result={preview} />
      )}
    </Stack>
  );

  const renderDone = () => (
    <Stack gap={2}>
      {result?.beforeSnapshot ? (
        <Alert severity="info">
          A safety restore point was saved first: &quot;{result.beforeSnapshot.name}&quot;. It is in
          the list and can be restored too.
        </Alert>
      ) : null}
      <EventSnapshotPreview result={result} applied />
    </Stack>
  );

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography component="div" variant="h6">
            Event History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {eventName || 'Restore a previous point'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          {step === 'list' ? renderList() : null}
          {step === 'review' ? renderReview() : null}
          {step === 'done' ? renderDone() : null}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {step === 'list' ? <Button onClick={handleClose}>Close</Button> : null}
          {step === 'review' ? (
            <>
              <Button
                onClick={() => {
                  setStep('list');
                  setActionError(null);
                }}
                disabled={applying}
              >
                Back
              </Button>
              <PrimaryButton
                variant="contained"
                color="warning"
                startIcon={applying ? null : <RestoreIcon />}
                onClick={handleApply}
                disabled={
                  applying || previewLoading || !preview?.previewHash || Boolean(preview?.blocked)
                }
              >
                {applying ? <CircularProgress size={20} color="inherit" /> : 'Restore this point'}
              </PrimaryButton>
            </>
          ) : null}
          {step === 'done' ? (
            <Button variant="contained" onClick={handleClose}>
              Close
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!deleting) {
            setDeleteTarget(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete restore point?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            &quot;{deleteTarget?.name}&quot; will be removed permanently. This cannot be undone.
          </DialogContentText>
          {deleteError ? (
            <Box mt={2}>
              <Alert severity="error">{errorMessage(deleteError)}</Alert>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EventHistoryDialog;
